import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Check, ChevronDown, ChevronRight, Copy, UploadCloud, X, FileText } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatBytes, initials } from '@/lib/format';
import { useAnnounce } from '@/lib/hooks';
import { IconButton } from './Button';

// ---------------------------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------------------------

const avatarSizes = { xs: 'h-6 w-6 text-2xs', sm: 'h-8 w-8 text-xs', md: 'h-10 w-10 text-sm', lg: 'h-14 w-14 text-lg', xl: 'h-20 w-20 text-2xl' } as const;
const avatarTints = [
  'bg-sapphire-100 text-sapphire-800 dark:bg-sapphire-700/40 dark:text-sapphire-100',
  'bg-ice-100 text-ice-700 dark:bg-ice-500/20 dark:text-ice-300',
  'bg-navy-100 text-navy-700 dark:bg-navy-700/50 dark:text-navy-100',
  'bg-sapphire-200 text-sapphire-900 dark:bg-sapphire-600/40 dark:text-sapphire-50',
  'bg-platinum-100 text-platinum-700 dark:bg-platinum-700/40 dark:text-platinum-100',
] as const;

/** Stable small hash used for deterministic colours (avatars, book jackets). */
export function hashString(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export interface AvatarProps {
  name?: string | null;
  src?: string | null;
  size?: keyof typeof avatarSizes;
  status?: 'online' | 'away' | 'offline';
  className?: string;
}

export function Avatar({ name, src, size = 'md', status, className }: AvatarProps) {
  const [broken, setBroken] = useState(false);
  const tint = avatarTints[hashString(name ?? '?') % avatarTints.length];
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      {src && !broken ? (
        <img src={src} alt={name ?? ''} loading="lazy" onError={() => setBroken(true)} className={cn('rounded-full object-cover ring-1 ring-line', avatarSizes[size])} />
      ) : (
        <span role="img" aria-label={name ?? 'Unknown'} className={cn('inline-flex select-none items-center justify-center rounded-full font-semibold ring-1 ring-inset ring-white/40', tint, avatarSizes[size])}>
          {initials(name)}
        </span>
      )}
      {status && (
        <span
          className={cn('absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ring-surface', status === 'online' ? 'bg-success-500' : status === 'away' ? 'bg-warning-500' : 'bg-platinum-400')}
          aria-label={status}
        />
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------------------------
// Kbd
// ---------------------------------------------------------------------------------------------

export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd className={cn('inline-flex min-w-[1.4rem] items-center justify-center rounded-md border border-line-strong bg-surface px-1.5 py-0.5 font-mono text-2xs font-medium text-ink-muted shadow-[0_1px_0_rgb(var(--lum-border-strong))]', className)}>
      {children}
    </kbd>
  );
}

// ---------------------------------------------------------------------------------------------
// Breadcrumbs
// ---------------------------------------------------------------------------------------------

export interface Crumb {
  label: string;
  to?: string;
}

export function Breadcrumbs({ items, className }: { items: Crumb[]; className?: string }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex flex-wrap items-center gap-1 text-xs text-ink-muted">
        {items.map((crumb, i) => {
          const last = i === items.length - 1;
          return (
            <li key={`${crumb.label}-${i}`} className="flex min-w-0 items-center gap-1">
              {crumb.to && !last ? (
                <Link to={crumb.to} className="max-w-[12rem] truncate rounded px-1 py-0.5 transition hover:bg-sunken hover:text-ink">
                  {crumb.label}
                </Link>
              ) : (
                <span aria-current={last ? 'page' : undefined} className={cn('max-w-[16rem] truncate px-1 py-0.5', last && 'font-medium text-ink')}>
                  {crumb.label}
                </span>
              )}
              {!last && <ChevronRight className="h-3 w-3 shrink-0 text-ink-subtle" aria-hidden="true" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ---------------------------------------------------------------------------------------------
// PageHeader
// ---------------------------------------------------------------------------------------------

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  breadcrumbs?: Crumb[];
  actions?: ReactNode;
  /** Badges / meta shown next to the title. */
  meta?: ReactNode;
  /** Tabs (or NavTabs) rendered flush under the title. */
  tabs?: ReactNode;
  className?: string;
}

/** The title block of every screen: Fraunces title, optional breadcrumbs, actions on the right, tabs underneath. */
export function PageHeader({ title, description, eyebrow, breadcrumbs, actions, meta, tabs, className }: PageHeaderProps) {
  return (
    <header className={cn('mb-6', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} className="mb-2 -ml-1" />}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          {eyebrow && <p className="mb-1 text-2xs font-semibold uppercase tracking-[0.14em] text-sapphire-700 dark:text-sapphire-300">{eyebrow}</p>}
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-2xl font-semibold tracking-tight text-ink sm:text-[1.75rem] sm:leading-9">{title}</h1>
            {meta}
          </div>
          {description && <p className="mt-1 max-w-2xl text-sm text-ink-muted">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {tabs && <div className="mt-5 -mb-px border-b border-line">{tabs}</div>}
    </header>
  );
}

// ---------------------------------------------------------------------------------------------
// Accordion
// ---------------------------------------------------------------------------------------------

export interface AccordionItem {
  id: string;
  title: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

export function Accordion({ items, multiple, defaultOpen = [], className }: { items: AccordionItem[]; multiple?: boolean; defaultOpen?: string[]; className?: string }) {
  const [open, setOpen] = useState<string[]>(defaultOpen);
  const base = useId();
  const toggle = (id: string): void =>
    setOpen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : multiple ? [...prev, id] : [id]));
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button[data-accordion-trigger]:not(:disabled)'));
    const i = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (i < 0) {
      return;
    }
    const target = event.key === 'ArrowDown' ? buttons[(i + 1) % buttons.length] : event.key === 'ArrowUp' ? buttons[(i - 1 + buttons.length) % buttons.length] : event.key === 'Home' ? buttons[0] : event.key === 'End' ? buttons[buttons.length - 1] : undefined;
    if (target) {
      event.preventDefault();
      target.focus();
    }
  };
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- key handler only moves focus between the real buttons
    <div className={cn('divide-y divide-line rounded-card bg-surface shadow-card', className)} onKeyDown={onKeyDown}>
      {items.map((item) => {
        const isOpen = open.includes(item.id);
        return (
          <div key={item.id}>
            <h3>
              <button
                type="button"
                data-accordion-trigger
                id={`${base}-${item.id}-t`}
                aria-expanded={isOpen}
                aria-controls={`${base}-${item.id}-p`}
                disabled={item.disabled}
                onClick={() => toggle(item.id)}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left text-sm font-medium text-ink transition hover:bg-sunken/60 disabled:opacity-50"
              >
                {item.title}
                <ChevronDown className={cn('h-4 w-4 shrink-0 text-ink-subtle transition-transform duration-200', isOpen && 'rotate-180')} aria-hidden="true" />
              </button>
            </h3>
            <div id={`${base}-${item.id}-p`} role="region" aria-labelledby={`${base}-${item.id}-t`} hidden={!isOpen} className="px-5 pb-5 text-sm text-ink-muted">
              {item.content}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------------------------------

export interface Step {
  id: string;
  label: string;
  description?: string;
}

/** Horizontal progress through a multi-step flow (import wizard, patron registration). `current` is a 0-based index. */
export function Stepper({ steps, current, className }: { steps: Step[]; current: number; className?: string }) {
  return (
    <ol className={cn('flex w-full items-start', className)} aria-label="Progress">
      {steps.map((step, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={step.id} className="relative flex flex-1 flex-col items-center text-center" aria-current={active ? 'step' : undefined}>
            {i > 0 && <span className={cn('absolute top-4 h-0.5 -translate-y-1/2', done || active ? 'bg-sapphire-500' : 'bg-line-strong')} aria-hidden="true" style={{ left: '-50%', right: '50%' }} />}
            <span
              className={cn(
                'relative z-10 grid h-8 w-8 place-items-center rounded-full text-xs font-semibold ring-4 ring-canvas transition',
                done && 'bg-sapphire-600 text-white',
                active && 'btn-sheen text-white shadow-glow',
                !done && !active && 'border border-line-strong bg-surface text-ink-muted',
              )}
            >
              {done ? <Check className="h-4 w-4" strokeWidth={3} aria-hidden="true" /> : i + 1}
              <span className="sr-only">{done ? ' (completed)' : active ? ' (current)' : ''}</span>
            </span>
            <span className={cn('mt-2 text-xs font-medium', active ? 'text-ink' : 'text-ink-muted')}>{step.label}</span>
            {step.description && <span className="hidden text-2xs text-ink-subtle sm:block">{step.description}</span>}
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------------------------

export interface TimelineItem {
  id: string | number;
  title: ReactNode;
  description?: ReactNode;
  time?: ReactNode;
  timestamp?: ReactNode;
  icon?: ReactNode;
  tone?: 'brand' | 'success' | 'warning' | 'danger' | 'neutral';
}

const timelineDot = {
  brand: 'bg-sapphire-100 text-sapphire-700 ring-sapphire-200 dark:bg-sapphire-700/30 dark:text-sapphire-200 dark:ring-sapphire-500/30',
  success: 'bg-success-50 text-success-700 ring-success-100 dark:bg-success-500/15 dark:text-success-500 dark:ring-success-500/25',
  warning: 'bg-warning-50 text-warning-700 ring-warning-100 dark:bg-warning-500/15 dark:text-warning-500 dark:ring-warning-500/25',
  danger: 'bg-danger-50 text-danger-700 ring-danger-100 dark:bg-danger-500/15 dark:text-danger-500 dark:ring-danger-500/25',
  neutral: 'bg-navy-100 text-navy-700 ring-navy-200 dark:bg-navy-700/40 dark:text-navy-100 dark:ring-navy-600/60',
} as const;

/** Vertical activity feed (patron history, audit trail, serial claims). */
export function Timeline({ items, className }: { items: TimelineItem[]; className?: string }) {
  return (
    <ol className={cn('relative space-y-5', className)}>
      {items.map((item, i) => (
        <li key={item.id} className="relative flex gap-3">
          {i < items.length - 1 && <span className="absolute bottom-[-1.25rem] left-4 top-8 w-px bg-line-strong" aria-hidden="true" />}
          <span className={cn('relative z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full ring-1 ring-inset [&>svg]:h-4 [&>svg]:w-4', timelineDot[item.tone ?? 'brand'])}>{item.icon ?? <span className="h-2 w-2 rounded-full bg-current" />}</span>
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <p className="text-sm font-medium text-ink">{item.title}</p>
              {(item.time ?? item.timestamp) && <p className="text-xs text-ink-subtle">{item.time ?? item.timestamp}</p>}
            </div>
            {item.description && <p className="mt-0.5 text-sm text-ink-muted">{item.description}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------------------------
// CopyButton
// ---------------------------------------------------------------------------------------------

/** Copies text and confirms with a check mark + a screen-reader announcement. */
export function CopyButton({ value, label = 'Copy', className }: { value: string; label?: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const announce = useAnnounce();
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const area = document.createElement('textarea');
      area.value = value;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.append(area);
      area.select();
      document.execCommand('copy');
      area.remove();
    }
    setCopied(true);
    announce('Copied to clipboard');
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1600);
  };
  return (
    <IconButton
      label={copied ? 'Copied' : label}
      size="sm"
      className={className}
      icon={copied ? <Check className="h-4 w-4 text-success-600" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
      onClick={() => void copy()}
    />
  );
}

// ---------------------------------------------------------------------------------------------
// FileDropzone
// ---------------------------------------------------------------------------------------------

export interface FileDropzoneProps {
  onFiles?: (files: File[]) => void;
  onDrop?: (files: File[]) => void;
  /** e.g. ".mrc,.xml,.csv" or "image/*" */
  accept?: string;
  multiple?: boolean;
  maxSizeBytes?: number;
  label?: string;
  hint?: ReactNode;
  disabled?: boolean;
  /** Currently chosen files (rendered as a removable list). */
  files?: File[];
  onRemove?: (file: File) => void;
  className?: string;
}

export interface FileValidationError {
  message: string;
  file: File;
  reason: string;
}

/** Validate dropped files against accept/maxSize. Exported for tests and for pages that need the same rules. */
export function validateFiles(files: File[], options: { accept?: string; maxSizeBytes?: number; maxSizeMb?: number; maxFiles?: number }): { ok: File[]; rejected: { file: File; reason: string }[]; errors: FileValidationError[] } {
  const accepts = (options.accept ?? '').split(',').map((a) => a.trim().toLowerCase()).filter(Boolean);
  const ok: File[] = [];
  const rejected: { file: File; reason: string }[] = [];
  const maxBytes = options.maxSizeBytes ?? (options.maxSizeMb ? options.maxSizeMb * 1024 * 1024 : undefined);
  for (const file of files) {
    const name = file.name.toLowerCase();
    const matchesType = accepts.length === 0 || accepts.some((a) => (a.startsWith('.') ? name.endsWith(a) : a.endsWith('/*') ? file.type.startsWith(a.slice(0, -1)) : file.type === a));
    if (!matchesType) {
      rejected.push({ file, reason: 'This file type is not supported' });
    } else if (maxBytes && file.size > maxBytes) {
      rejected.push({ file, reason: `Larger than ${formatBytes(maxBytes)}` });
    } else if (options.maxFiles && ok.length >= options.maxFiles) {
      rejected.push({ file, reason: `Exceeds max allowed files (${options.maxFiles})` });
    } else {
      ok.push(file);
    }
  }
  const errors: FileValidationError[] = rejected.map((r) => ({ file: r.file, reason: r.reason, message: `${r.file.name}: ${r.reason}` }));
  return { ok, rejected, errors };
}

export function FileDropzone({ onFiles, onDrop, accept, multiple, maxSizeBytes, label = 'Drop files here or browse', hint, disabled, files = [], onRemove, className }: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const announce = useAnnounce();

  const handle = (list: FileList | null): void => {
    if (!list || disabled) {
      return;
    }
    const { ok, rejected } = validateFiles(Array.from(list), { accept, maxSizeBytes });
    const messages = rejected.map((r) => `${r.file.name}: ${r.reason}`);
    setErrors(messages);
    if (messages.length) {
      announce(messages.join('. '), 'assertive');
    }
    if (ok.length) {
      const chosen = multiple ? ok : ok.slice(0, 1);
      onFiles?.(chosen);
      onDrop?.(chosen);
    }
  };

  return (
    <div className={className}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handle(e.dataTransfer.files);
        }}
        className={cn(
          'group flex w-full flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed px-6 py-10 text-center transition',
          dragging ? 'border-sapphire-500 bg-sapphire-50 dark:bg-sapphire-900/30' : 'border-line-strong bg-surface hover:border-sapphire-400 hover:bg-sapphire-50/50 dark:hover:bg-sapphire-900/20',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <span className="grid h-12 w-12 place-items-center rounded-xl bg-sapphire-100 text-sapphire-700 transition group-hover:scale-105 dark:bg-sapphire-700/30 dark:text-sapphire-200">
          <UploadCloud className="h-6 w-6" aria-hidden="true" />
        </span>
        <span className="text-sm font-medium text-ink">{label}</span>
        {hint && <span className="text-xs text-ink-muted">{hint}</span>}
      </button>
      <input ref={inputRef} type="file" className="sr-only" tabIndex={-1} accept={accept} multiple={multiple} disabled={disabled} onChange={(e) => {
        handle(e.target.files);
        e.target.value = '';
      }} />
      {errors.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs font-medium text-danger-700 dark:text-danger-500" role="alert">
          {errors.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
      {files.length > 0 && (
        <ul className="mt-3 divide-y divide-line rounded-xl border border-line bg-surface">
          {files.map((file) => (
            <li key={`${file.name}-${file.size}`} className="flex items-center gap-3 px-3 py-2">
              <FileText className="h-4 w-4 shrink-0 text-sapphire-600" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-sm text-ink">{file.name}</span>
              <span className="text-xs text-ink-subtle">{formatBytes(file.size)}</span>
              {onRemove && <IconButton label={`Remove ${file.name}`} size="xs" icon={<X className="h-3.5 w-3.5" aria-hidden="true" />} onClick={() => onRemove(file)} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
